import React from 'react';
import { Navigate } from 'react-router-dom';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import { Form, FormGroup, FormControl, Button, Row, Col } from 'react-bootstrap';
import SharedDataTable from '../../_components/Shared/SharedDataTable';
import config from '../../../config';
import moment from 'moment';
import { handleFocusInScrollField } from './invKeyboardScroll';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import {
    InventorySummaryExportTable,
    RECEIVING_SUMMARY_EXPORT_COLUMNS,
    exportInventorySummary,
    mapReceivingToExportRow,
} from './invSummaryExport';

function normalizeDateFilterInput(input) {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const ddmmyyyy = moment(raw, 'DD/MM/YYYY', true);
    if (ddmmyyyy.isValid()) return ddmmyyyy.format('YYYY-MM-DD');
    const iso = moment(raw, 'YYYY-MM-DD', true);
    if (iso.isValid()) return iso.format('YYYY-MM-DD');
    return '';
}

class ReceivingSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            allData: [],
            loading: false,
            company: 0,
            searchKeyword: '',
            searchReceivingDate: '',
            searchStatus: '',
        };
    }

    componentDidMount() {
        this.loadUserData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.company !== this.state.company && this.state.company) {
            this.loadReceivingData();
        }
    }

    loadUserData = () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            if (userData && userData.accountId) {
                this.setState({ company: userData.accountId }, () => {
                    this.loadReceivingData();
                });
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    }

    loadReceivingData = async () => {
        if (!this.state.company) return;

        this.setState({ loading: true });
        try {
            const response = await fetch(
                `${config.apiUrl}/getReceiving?company=${this.state.company}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            let data = await response.json();
            
            if (Array.isArray(data)) {
                const formattedData = data.map((item, index) => {
                    const reportNo = item.createdAt && item.id 
                        ? moment(item.createdAt).format('YYYYMMDD') + 'R' + item.id
                        : item.id || '-';
                    
                    return {
                        no: index + 1,
                        reportNo: reportNo,
                        id: item.id,
                        partNo: item.partNo || '-',
                        description: item.description || '-',
                        receivingDateTime: item.createdAt 
                            ? moment(item.createdAt).format('DD/MM/YYYY')
                            : '-',
                        invoiceGrn: item.invoiceNo || item.grnNo || '-',
                        lotQty: item.quantity || 0,
                        lotNo: item.lotNo || '-',
                        storageLocation: item.storageLocation || '',
                        supplier: item.supplier || '-',
                        status: item.status === 'rejected' 
                            ? `Rejected. ${item.rejectReason || 'No reason provided'}`
                            : item.status === 'accepted' 
                                ? 'Accepted'
                                : item.status || 'Pending',
                        statusRaw: item.status || 'pending',
                        rejectReason: item.rejectReason || '',
                        type: item.type || '',
                        remarks: item.remarks || '-',
                        createdAt: item.createdAt,
                        sourceItem: item,
                    };
                });
                
                // Store all data for filtering
                this.setState({ allData: formattedData }, () => {
                    this.applyFilters();
                });
            } else {
                this.setState({ data: [], allData: [], loading: false });
            }
        } catch (error) {
            console.error('Error loading receiving data:', error);
            this.setState({ loading: false });
        }
    }

    applyFilters = () => {
        let filteredData = [...this.state.allData];

        // Filter by search keyword (Part No, Supplier, Invoice No/GRN, Type of Discrepancy)
        if (this.state.searchKeyword) {
            const keyword = this.state.searchKeyword.toLowerCase();
            filteredData = filteredData.filter(item => {
                const partNo = (item.partNo || '').toLowerCase();
                const supplier = (item.supplier || '').toLowerCase();
                const invoiceGrn = (item.invoiceGrn || '').toLowerCase();
                const type = (item.type || '').toLowerCase();
                const storageLoc = (item.storageLocation || '').toLowerCase();
                const lotNo = (item.lotNo || '').toLowerCase();
                
                return partNo.includes(keyword) || 
                       supplier.includes(keyword) || 
                       invoiceGrn.includes(keyword) ||
                       type.includes(keyword) ||
                       storageLoc.includes(keyword) ||
                       lotNo.includes(keyword);
            });
        }

        // Filter by receiving date
        if (this.state.searchReceivingDate) {
            const searchDate = normalizeDateFilterInput(this.state.searchReceivingDate);
            if (!searchDate) {
                filteredData = [];
            } else {
            filteredData = filteredData.filter(item => {
                if (item.createdAt) {
                    const itemDate = moment(item.createdAt).format('YYYY-MM-DD');
                    return itemDate === searchDate;
                }
                return false;
            });
            }
        }

        // Filter by status
        if (this.state.searchStatus) {
            if (this.state.searchStatus === 'accepted') {
                filteredData = filteredData.filter(item => item.statusRaw === 'accepted');
            } else if (this.state.searchStatus === 'rejected') {
                filteredData = filteredData.filter(item => item.statusRaw === 'rejected');
            }
        }

        // Update row numbers
        filteredData = filteredData.map((item, index) => ({
            ...item,
            no: index + 1
        }));

        this.setState({ data: filteredData, loading: false });
    }

    handleSearch = () => {
        this.applyFilters();
    }

    handleReset = () => {
        this.setState({
            searchKeyword: '',
            searchReceivingDate: '',
            searchStatus: '',
        }, () => {
            this.applyFilters();
        });
    }

    exportExcel = () => {
        const rows = (this.state.data || []).map((row, index) =>
            mapReceivingToExportRow(row.sourceItem || row, index)
        );
        if (!rows.length) {
            alert('No records to export. Adjust filters or refresh the list.');
            return;
        }
        exportInventorySummary('receiving-summary-export', 'Receiving Summary', 'Receiving_Summary');
    }

    getExportRows = () => (this.state.data || []).map((row, index) =>
        mapReceivingToExportRow(row.sourceItem || row, index)
    )

    columns = [
        {
            name: 'No',
            selector: row => row.no,
            width: '100px',
            sortable: true,
        },
        {
            name: 'Report No',
            selector: row => row.reportNo,
            width: '180px',
            sortable: true,
            cell: (row) => (
                <div>
                    <a 
                        href="#"
                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (row.id) {
                                window.location.hash = `#/receiving-summary/${row.id}`;
                            }
                        }}
                    >
                        {row.reportNo}
                    </a>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
        {
            name: 'Part No',
            selector: row => row.partNo,
            width: '120px',
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description,
            width: '200px',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Receiving Date & Time',
            selector: row => row.receivingDateTime,
            width: '180px',
            sortable: true,
        },
        {
            name: 'Invoice/GRN',
            selector: row => row.invoiceGrn,
            width: '120px',
            sortable: true,
        },
        {
            name: 'Lot Qty',
            selector: row => row.lotQty,
            width: '100px',
            sortable: true,
            right: true,
        },
        {
            name: 'Storage Location',
            selector: row => row.storageLocation,
            width: '220px',
            sortable: true,
            wrap: true,
            cell: (row) => (
                <div title={row.storageLocation || ''}>
                    <div>{row.storageLocation ? row.storageLocation : '-'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Lot No: {row.lotNo || '-'}
                    </div>
                </div>
            ),
        },
        {
            name: 'Supplier',
            selector: row => row.supplier,
            width: '150px',
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            width: '200px',
            sortable: true,
            wrap: true,
            cell: (row) => (
                <span style={{
                    color: row.status.includes('Rejected') ? '#dc3545' : 
                           row.status === 'Accepted' ? '#28a745' : '#ffc107',
                    fontWeight: '500'
                }}>
                    {row.status}
                </span>
            ),
        },
        {
            name: 'Remarks',
            selector: row => row.remarks,
            width: '200px',
            sortable: true,
            wrap: true,
        },
    ];

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        return (
            <div className="container" onFocusCapture={handleFocusInScrollField}>
                <div style={{ marginTop: '20px' }}>
                    <h4>Receiving Summary</h4>
                    <p className="text-muted">View and manage receiving records</p>
                    
                    <div style={{ marginBottom: '20px', marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <Form>
                            <Row className="align-items-end">
                                <Col sm={12} md={4}>
                                    <FormGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Search by Part No, Supplier, Invoice/GRN, Type, Storage Location"
                                            value={this.state.searchKeyword}
                                            onChange={(e) => this.setState({ searchKeyword: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={2}>
                                    <FormGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Receiving Date (DD/MM/YYYY)"
                                            value={this.state.searchReceivingDate}
                                            onChange={(e) => this.setState({ searchReceivingDate: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={2}>
                                    <FormGroup>
                                        <FormControl
                                            as="select"
                                            value={this.state.searchStatus}
                                            onChange={(e) => this.setState({ searchStatus: e.target.value })}
                                        >
                                            <option value="">All Status</option>
                                            <option value="accepted">Accept</option>
                                            <option value="rejected">Reject</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={4}>
                                    <Button
                                        variant="primary"
                                        onClick={this.handleSearch}
                                        style={{ marginRight: '10px' }}
                                        disabled={this.state.loading}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={this.handleReset}
                                        style={{ marginRight: '10px' }}
                                        disabled={this.state.loading}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="info"
                                        onClick={this.loadReceivingData}
                                        disabled={this.state.loading}
                                        style={{ marginRight: '10px' }}
                                    >
                                        {this.state.loading ? 'Loading...' : 'Refresh'}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={this.exportExcel}
                                        disabled={this.state.loading || !this.state.data.length}
                                    >
                                        Export Excel
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <SharedDataTable
                        columns={this.columns}
                        data={this.state.data}
                        progressPending={this.state.loading}
                        progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading receiving summary" />}
                        pagination
                        paginationPerPage={30}
                        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                        responsive
                        noDataComponent="No receiving records found"
                    />

                    <InventorySummaryExportTable
                        tableId="receiving-summary-export"
                        columns={RECEIVING_SUMMARY_EXPORT_COLUMNS}
                        rows={this.getExportRows()}
                    />
                </div>
            </div>
        );
    }
}

export { ReceivingSummary };
